import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";

/**
 * `GET /api/runs/{run_id}/inspection/affdo/scan-geometry` — one torsion's
 * per-scan-point geometry ADDRESS plus that point's index-aligned provenance
 * (VIZ U5.2a, consumed by U5.2b).
 *
 * The wire format mirrors `src/flowstack/server/routes/affdo.py`
 * (`TorsionScanGeometryResponse`). The authoritative statement of what these
 * fields mean — and of the two things they deliberately do NOT certify — is
 * `docs/source/dev/reference/scan-geometry-provenance.rst`; it is not restated
 * here. What this module owns is the wire shape and the fetch.
 */

/**
 * Where one scan point's geometry lives. An **address**, never bytes and never
 * a presigned URL.
 *
 * `download_url` is the API's own authenticated payload route, which re-reads
 * the artifact row under the CALLER's tenant before it touches storage. So a
 * 404 here is a NORMAL outcome — the row may not exist under this tenant, the
 * payload may never have been registered under that name, or the artifact may
 * have been lifecycle-collected — and the viewer renders it as a state rather
 * than as a failure.
 */
export interface ScanGeometryRef {
  content_hash: string;
  payload_name: string;
  download_url: string;
  /** The record's geometry IDENTITY (`scan_geometry_digests`). Cache frames on
   * THIS, never on `download_url`: the stored URI is a location the dispatch
   * layer rewrites at every publish/stage hop and the record's own content hash
   * excludes it. `null` when the record carries no digest list. */
  digest: string | null;
}

/**
 * One scan point.
 *
 * `geometry_status` and `geometry_source` are typed as bare `string` ON
 * PURPOSE, and the two reasons differ:
 *
 * - `geometry_source` is the raw `scan_geometry_sources` value, and that list
 *   is APPEND-ONLY documentation rather than a validation gate. Narrowing it to
 *   a union here would make a future member a TypeScript error at the boundary
 *   and tempt the next reader to "fix" it by coercing — the exact
 *   pattern-match-on-two-strings bug this unit's tests exist to catch.
 *   `geometry_source_known` is the server's own answer to "does this release
 *   recognise it", and the client reads THAT, never the string's shape.
 * - `geometry_status` is a closed three-set today, but it is a server-side
 *   vocabulary that can gain a member without a client release, and every
 *   consumer of it in this app routes an unrecognised value to the same
 *   "cannot show this" state as `unresolvable`. A union would turn a
 *   forward-compatible degrade into a build break.
 */
export interface ScanPointGeometry {
  /** Position in the SCAN's own grid (`qm_profile.angles_deg`). */
  index: number;
  /** That grid's angle. `null` only for a non-finite value in a malformed blob. */
  angle_deg: number | null;
  geometry: ScanGeometryRef | null;
  /** `addressable` | `not_recorded` | `unresolvable` — see the note above. */
  geometry_status: string;
  /** Raw and verbatim; `null` only when the key is absent from the record. */
  geometry_source: string | null;
  /** Whether THIS release recognises `geometry_source`. A reading aid, never a filter. */
  geometry_source_known: boolean;
}

export interface TorsionScanGeometryResponse {
  fragment_id: string;
  torsion_id: string;
  /** Producer-assigned and NOT unique across re-runs — never the field to
   * compare on. `content_hash` is the discriminating term. */
  torsion_scan_result_id: string;
  /** The `TorsionScanResult` catalog row these points were read from. */
  content_hash: string;
  /** `sequential` | `wavefront` | `ml_reference`, for display. */
  method: string | null;
  /** The catalog row's own `created_at` — the key the selection sorted on.
   * Catalog-assigned, always UTC-aware, ISO-8601 with an explicit offset. */
  created_at: string;
  n_points: number;
  /** `false` when the record carries no usable index-aligned
   * `scan_geometry_sources`. Every point then reads UNKNOWN — never
   * "all optimized". */
  provenance_available: boolean;
  points: ScanPointGeometry[];
}

export function scanGeometryQueryKey(
  runId: string,
  fragmentId: string,
  torsionId: string,
  refreshKey?: string
): readonly unknown[] {
  return [
    "runs",
    runId,
    "affdo",
    "scan-geometry",
    fragmentId,
    torsionId,
    refreshKey ?? null,
  ] as const;
}

/**
 * One torsion's scan geometry + provenance.
 *
 * `refreshKey` threads the run view's DAG-poll fingerprint the same way the
 * other AFFDO hooks do, so a re-scan landing mid-run is picked up on the poll
 * that is already happening rather than by a second `refetchInterval`
 * (flowstack#950 §0.0).
 *
 * `retry: false` because the dominant non-200 here is `404
 * torsion_scan_not_found`, which is a fact about the run — this torsion has no
 * `TorsionScanResult`, or this deployment has no database wired — not a
 * transient fault. Retrying re-asks a question that will be answered
 * identically, and delays the state the panel needs to render.
 */
export function useScanGeometry(
  runId: string,
  fragmentId: string,
  torsionId: string,
  options?: { enabled?: boolean; refreshKey?: string }
) {
  const enabled =
    (options?.enabled ?? true) && Boolean(runId) && Boolean(fragmentId) && Boolean(torsionId);
  return useQuery({
    queryKey: scanGeometryQueryKey(runId, fragmentId, torsionId, options?.refreshKey),
    queryFn: async (): Promise<TorsionScanGeometryResponse> =>
      apiFetch<TorsionScanGeometryResponse>(
        `/api/runs/${encodeURIComponent(runId)}/inspection/affdo/scan-geometry` +
          `?fragment_id=${encodeURIComponent(fragmentId)}` +
          `&torsion_id=${encodeURIComponent(torsionId)}`
      ),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}
